'use client';

/**
 * Client-Side Auth Helper Utilities
 * Provides helper functions for authentication flows
 */

/**
 * Build Cognito Hosted UI logout URL
 * Calls the server-side API to generate the proper logout URL
 *
 * @returns The logout URL to redirect to, or null if unavailable
 *
 * @example
 * ```typescript
 * const logoutUrl = await buildCognitoLogoutUrl();
 * if (logoutUrl) {
 *   window.location.href = logoutUrl;
 * }
 * ```
 */
export async function buildCognitoLogoutUrl(): Promise<string | null> {
  try {
    const res = await fetch('/api/auth/logout-url', { cache: 'no-store' });
    if (!res.ok) {
      console.error('[auth-helpers] Failed to fetch logout URL:', res.status);
      return null;
    }

    const data = (await res.json()) as Record<string, unknown>;
    const url = typeof data.url === 'string' ? data.url : null;

    if (!url) {
      console.error('[auth-helpers] No logout URL returned:', data);
    }

    return url;
  } catch (error) {
    console.error('[auth-helpers] Error building logout URL:', error);
    return null;
  }
}

/**
 * Perform complete logout flow
 * Signs out of NextAuth session and redirects to Cognito logout
 *
 * @param signOut - NextAuth signOut function
 *
 * @example
 * ```typescript
 * import { signOut } from 'next-auth/react';
 * import { performLogout } from '@/lib/auth-helpers';
 *
 * await performLogout(signOut);
 * ```
 */
export async function performLogout(
  signOut: (options?: any) => Promise<any>
): Promise<void> {
  try {
    // Get Cognito logout URL first
    const logoutUrl = await buildCognitoLogoutUrl();

    // Sign out of NextAuth session
    await signOut({ redirect: false });

    // Redirect to Cognito Hosted UI logout
    if (logoutUrl) {
      window.location.href = logoutUrl;
    } else {
      // Fallback: redirect to login
      window.location.href = '/auth/login';
    }
  } catch (error) {
    console.error('[auth-helpers] Logout error:', error);
    // Fallback: redirect to login
    window.location.href = '/auth/login';
  }
}

/**
 * Check if session has authentication error
 * Useful for detecting token refresh failures
 *
 * @param session - NextAuth session object
 * @returns True if session has an error
 *
 * @example
 * ```typescript
 * const session = await getSession();
 * if (hasAuthError(session)) {
 *   // Redirect to login
 *   router.push('/auth/login');
 * }
 * ```
 */
export function hasAuthError(
  session: { error?: string } | null | undefined
): boolean {
  return !!(session && session.error);
}

/**
 * Check if access token is expiring soon
 * Useful for triggering proactive refresh
 *
 * @param expiresAt - Token expiration timestamp (epoch seconds)
 * @param bufferSeconds - How many seconds before expiry to consider "expiring" (default: 300 = 5 minutes)
 * @returns True if token is expiring within buffer time
 */
export function isTokenExpiring(
  expiresAt: number | undefined,
  bufferSeconds: number = 300
): boolean {
  if (!expiresAt) return false;
  const now = Math.floor(Date.now() / 1000);
  return now >= expiresAt - bufferSeconds;
}
